import { useState, useEffect, useMemo } from 'react';
import CloseIcon from '../../../assets/icons/close.svg?react';
import { useUpdateDRResource } from '../../../hooks/queries/drResourceQueries';
import jejuRegionData from '../../../assets/jeju_region_en.json';
import Selector from '../../../components/Selector';
// {
//   "resourceName": "string",
//   "regionCode": "string",
//   "regionSido": "string",
//   "regionSigungu": "string",
//   "regionEupMyuonDong": "string",
//   "note": "string"
// }
const DRUpdateSideModal = ({onClose, onSuccess, target}) => {
  const [drUpdateObject, setDrUpdateObject] = useState({
    drResourceSysId: target?.DrResourceSysId,
    resourceName: target?.ResourceName,
    regionCode: target?.RegionCode,
    regionSido: target?.RegionSido,
    regionSigungu: target?.RegionSigungu,
    regionEupMyuonDong: target?.RegionEupMyuonDong,
    note: target?.Note,
    drType: target.DrType
  });

  // React Query mutation 훅을 컴포넌트 최상위 레벨에서 호출
  const updateDRResourceMutation = useUpdateDRResource();

  // 지역 데이터 처리 (SelectorItem 형태로 변환)
  const sidoOptions = useMemo(() => {
    const sidos = [...new Set(jejuRegionData.map(item => item.RegionSido))];
    return sidos.map(sido => ({
      id: sido,
      label: sido,
      data: { sido }
    }));
  }, []);

  const sigunguOptions = useMemo(() => {
    if (!drUpdateObject.regionSido) return [];
    const sigungus = [...new Set(
      jejuRegionData
        .filter(item => item.RegionSido === drUpdateObject.regionSido)
        .map(item => item.RegionSigungu)
    )];
    return sigungus.map(sigungu => ({
      id: sigungu,
      label: sigungu,
      data: { sigungu }
    }));
  }, [drUpdateObject.regionSido]);

  const eupmyeondongOptions = useMemo(() => {
    if (!drUpdateObject.regionSido || !drUpdateObject.regionSigungu) return [];
    const eupmyeondongs = [...new Set(
      jejuRegionData
        .filter(item => 
          item.RegionSido === drUpdateObject.regionSido && 
          item.RegionSigungu === drUpdateObject.regionSigungu
        )
        .map(item => item.RegionEupMyuonDong)
    )];
    return eupmyeondongs.map(eupmyeondong => ({
      id: eupmyeondong,
      label: eupmyeondong,
      data: { eupmyeondong }
    }));
  }, [drUpdateObject.regionSido, drUpdateObject.regionSigungu]);

  const drTypeOptions = useMemo(() => {
    return [{ id: '국민DR', label: '국민DR', data: '국민DR' }];
  }, []);

  // 지역 선택 시 관련 필드 초기화 (Selector의 onChange 형식에 맞게 수정)
  const handleRegionChange = (field, selectedItem) => {
    const value = selectedItem ? selectedItem.id : null;
    setDrUpdateObject(prev => {
      const newState = { ...prev, [field]: value };
      
      // 시도 변경 시 시군구, 읍면동 초기화
      if (field === 'regionSido') {
        newState.regionSigungu = null;
        newState.regionEupMyuonDong = null;
        newState.regionCode = null;
      }
      // 시군구 변경 시 읍면동 초기화
      else if (field === 'regionSigungu') {
        newState.regionEupMyuonDong = null;
        newState.regionCode = null;
      }
      // 읍면동 변경 시 지역코드 설정
      else if (field === 'regionEupMyuonDong') {
        const selectedRegion = jejuRegionData.find(item => 
          item.RegionSido === newState.regionSido &&
          item.RegionSigungu === newState.regionSigungu &&
          item.RegionEupMyuonDong === value
        );
        newState.regionCode = selectedRegion ? selectedRegion.RegionCode.toString() : null;
      }
      
      return newState;
    });
  };

  const handleDrTypeChange = (field, selectedItem) => {
    const value = selectedItem ? (selectedItem.label || selectedItem.id) : null;
    setDrUpdateObject(prev => {
      return {...prev, [field]: value};
    })
  }

  // target이 변경될 때 초기값 설정
  useEffect(() => {
    if(target){
      setDrUpdateObject({
        drResourceSysId: target.DrResourceSysId,
        resourceName: target.ResourceName,
        regionCode: target.RegionCode,
        regionSido: target.RegionSido,
        regionSigungu: target.RegionSigungu,
        regionEupMyuonDong: target.RegionEupMyuonDong,
        note: target.Note,
        drType: target.DrType
      });
    }
  }, [target]);

  const handleDrUpdateObject = (e) => {
    setDrUpdateObject(prev => ({ ...prev, [e.target.id]: e.target.value }));
  };

  // 현재 선택된 값을 SelectorItem 형태로 변환
  const getSelectedSido = useMemo(() => {
    if (!drUpdateObject.regionSido) return null;
    return sidoOptions.find(option => option.id === drUpdateObject.regionSido) || null;
  }, [drUpdateObject.regionSido, sidoOptions]);

  const getSelectedSigungu = useMemo(() => {
    if (!drUpdateObject.regionSigungu) return null;
    return sigunguOptions.find(option => option.id === drUpdateObject.regionSigungu) || null;
  }, [drUpdateObject.regionSigungu, sigunguOptions]);

  const getSelectedEupmyeondong = useMemo(() => {
    if (!drUpdateObject.regionEupMyuonDong) return null;
    return eupmyeondongOptions.find(option => option.id === drUpdateObject.regionEupMyuonDong) || null;
  }, [drUpdateObject.regionEupMyuonDong, eupmyeondongOptions]);

  const getSelectedDrType = useMemo(() => {
    if (!drUpdateObject.drType) return null;
    return (
      drTypeOptions.find(
        (option) =>
          option.id === drUpdateObject.drType || option.label === drUpdateObject.drType
      ) || { id: drUpdateObject.drType, label: drUpdateObject.drType }
    );
  }, [drUpdateObject.drType, drTypeOptions]);

  const handleDrUpdate = async () => {
    if(!drUpdateObject.resourceName || !drUpdateObject.regionCode || !drUpdateObject.regionSido || !drUpdateObject.regionSigungu || !drUpdateObject.regionEupMyuonDong){
      alert('필수 항목을 입력해주세요.');
      return;
    }
    
    try {
      await updateDRResourceMutation.mutateAsync({ 
        id: drUpdateObject.drResourceSysId, 
        data: drUpdateObject 
      });
      onSuccess(); // 수정 성공 시 콜백 호출
      onClose();
    } catch (error) {
      console.error('DR자원 수정 실패:', error);
      alert('DR자원 수정에 실패했습니다.');
    }
  };

  return (
    <div className="side-modal-background">
      <div className="side-modal-container">
        <div className="side-modal-header">
          <h3>DR자원 수정</h3>
          <div className="side-modal-close" onClick={onClose}>
            <CloseIcon />
          </div>
        </div>
        <div className="side-modal-content">
          <table className='transparent-table w-full'>
            <tr>  
              <td>
                <label htmlFor="resourceName" className='w-20 mr-10 text-b2-700 required'>DR자원 이름</label>
              </td>
              <td>
                <input value={drUpdateObject.resourceName || ''} type="text" id="resourceName" className='w-full input-primary' placeholder='DR자원 이름을 입력해주세요.' onChange={handleDrUpdateObject} autoComplete='off'/>
              </td>
            </tr> 
            <tr>
              <td>
                <label htmlFor="regionCode" className='w-20 mr-10 text-b2-700'>지역구분코드</label>
              </td>
              <td>
                <input value={drUpdateObject.regionCode || ''} type="text" id="regionCode" className='w-full input-primary bg-gray-50' placeholder='지역을 선택하면 자동으로 입력됩니다.' readOnly/>
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="regionSido" className='w-20 mr-10 text-b2-700 required'>시도</label>
              </td>
              <td>
                <Selector
                  mode="local"
                  options={sidoOptions}
                  value={getSelectedSido}
                  onChange={(item) => handleRegionChange('regionSido', item)}
                  placeholder="시도를 선택해주세요"
                  className="w-full"
                />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="regionSigungu" className='w-20 mr-10 text-b2-700 required'>시군구</label>
              </td>
              <td>
                <Selector
                  mode="local"
                  options={sigunguOptions}
                  value={getSelectedSigungu}
                  onChange={(item) => handleRegionChange('regionSigungu', item)}
                  placeholder="시군구를 선택해주세요"
                  disabled={!drUpdateObject.regionSido}
                  className="w-full"
                />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="regionEupMyuonDong" className='w-20 mr-10 text-b2-700 required'>읍면동</label>
              </td>
              <td>
                <Selector
                  mode="local"
                  options={eupmyeondongOptions}
                  value={getSelectedEupmyeondong}
                  onChange={(item) => handleRegionChange('regionEupMyuonDong', item)}
                  placeholder="읍면동을 선택해주세요"
                  disabled={!drUpdateObject.regionSigungu}
                  className="w-full"
                />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="drType" className='w-20 mr-10 text-b2-700 required'>참여 DR제도</label>
              </td>
              <td>
                <Selector
                  mode="local"
                  creatable={true}
                  options={drTypeOptions}
                  value={getSelectedDrType}
                  onChange={(item)=> handleDrTypeChange('drType', item)}
                  placeholder="참여 DR 제도를 선택해주세요"
                  className="w-full"
                />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="note" className='w-20 mr-10 text-b2-700'>비고</label>
              </td>
              <td>
                <input value={drUpdateObject.note || ''} id="note" className='w-full input-primary' placeholder='비고를 입력해주세요.' onChange={handleDrUpdateObject} autoComplete='off'/>
              </td>
            </tr>
          </table>
          <div className='flex justify-end'>
            <button 
              className='btn big-btn btn-primary w-full' 
              onClick={handleDrUpdate}
              disabled={updateDRResourceMutation.isPending}
            >
              {updateDRResourceMutation.isPending ? '수정 중...' : '수정하기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DRUpdateSideModal
